// components/PlanCard.jsx
import React, { useState } from 'react';
import { 
  Edit, 
  Trash2, 
  Check, 
  X, 
  Users, 
  Package, 
  Building2, 
  Shield, 
  Zap, 
  Gift, 
  Eye, 
  EyeOff,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const PlanCard = ({ plan, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Get data from plan with PlanLimits fallback
  const moduleCount = plan.moduleCount || 
                     (plan.limits?.modules ? Object.values(plan.limits.modules).filter(v => v).length : 0) ||
                     (plan.modules ? Object.values(plan.modules).filter(v => v).length : 0);
  
  const roleCount = plan.roleCount ||
                   (plan.limits?.roleAccess ? Object.values(plan.limits.roleAccess).filter(v => v).length : 0) ||
                   (plan.roleAccess ? Object.values(plan.roleAccess).filter(v => v).length : 0);
  
  const yearlyPrice = plan.pricing?.yearly?.amount || plan.pricing?.monthly?.amount * 12 || 0;
  
  const taxInfo = plan.tax;
  const hasTax = taxInfo && taxInfo.taxPercentage > 0;
  const taxDisplay = hasTax 
    ? `${taxInfo.taxPercentage}% ${taxInfo.taxName}${taxInfo.isTaxInclusive ? ' (incl.)' : ''}`
    : 'No tax';
  
  const hasTrial = plan.trial?.enabled || false;
  const trialDays = plan.trial?.durationDays || 0;

  // Check if plan has PlanLimits synced
  const hasPlanLimits = !!plan.limits;
  const limitsVersion = plan.limits?.version || 0;
  const lastSynced = plan.limits?.lastUpdated || plan.updatedAt;

  // Visibility badge
  const getVisibilityBadge = () => {
    const visibility = plan.visibility || 'public';
    const assignedCount = plan.assignedTenants?.length || 0;
    const invitedCount = plan.invitedTenants?.length || 0;
    
    if (visibility === 'public') {
      return { 
        label: 'Public', 
        icon: <Eye className="w-3 h-3" />, 
        color: 'text-emerald-600 bg-emerald-50 border-emerald-200' 
      };
    } else if (visibility === 'private') {
      return { 
        label: `Private (${assignedCount} tenants)`, 
        icon: <EyeOff className="w-3 h-3" />, 
        color: 'text-purple-600 bg-purple-50 border-purple-200' 
      };
    } else if (visibility === 'invitation') {
      return { 
        label: `Invitation (${invitedCount})`, 
        icon: <Gift className="w-3 h-3" />, 
        color: 'text-amber-600 bg-amber-50 border-amber-200' 
      };
    }
    return { 
      label: 'Public', 
      icon: <Eye className="w-3 h-3" />, 
      color: 'text-emerald-600 bg-emerald-50 border-emerald-200' 
    };
  };

  const visibilityBadge = getVisibilityBadge();

  // Get enabled modules and roles lists
  const getEnabledModules = () => {
    if (plan.limits?.modules) {
      return Object.entries(plan.limits.modules)
        .filter(([_, value]) => value === true)
        .map(([key]) => key);
    }
    if (plan.modules) {
      return Object.entries(plan.modules)
        .filter(([_, value]) => value === true)
        .map(([key]) => key);
    }
    return [];
  };

  const getEnabledRoles = () => {
    if (plan.limits?.roleAccess) {
      return Object.entries(plan.limits.roleAccess)
        .filter(([_, value]) => value === true)
        .map(([key]) => key);
    }
    if (plan.roleAccess) {
      return Object.entries(plan.roleAccess)
        .filter(([_, value]) => value === true)
        .map(([key]) => key);
    }
    return [];
  };

  const enabledModules = getEnabledModules();
  const enabledRoles = getEnabledRoles();

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Never';
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <div className={`bg-white rounded-2xl border p-5 hover:shadow-lg transition-all duration-300 group ${
      plan.isActive ? 'border-slate-200 hover:border-indigo-300' : 'border-slate-200 bg-slate-50/50'
    }`}>
      
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-slate-900 truncate">{plan.name}</h3>
            
            {/* Plan Type Badges */}
            {plan.type === 'free' && (
              <span className="px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-full flex-shrink-0">
                Free
              </span>
            )}
            {plan.type === 'enterprise' && (
              <span className="px-2 py-0.5 text-xs font-medium bg-purple-50 text-purple-700 rounded-full flex-shrink-0">
                Enterprise
              </span>
            )}
            {plan.type === 'paid' && (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-full flex-shrink-0">
                Paid
              </span>
            )}
            
            {/* Trial Badge */}
            {hasTrial && (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-full flex-shrink-0 flex items-center gap-1">
                <Gift className="w-3 h-3" />
                {trialDays}d trial
              </span>
            )}
            
            {/* PlanLimits Sync Badge */}
            {hasPlanLimits ? (
              <span className="px-2 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 rounded-full flex-shrink-0 flex items-center gap-1">
                <Check className="w-3 h-3" />
                v{limitsVersion}
              </span>
            ) : (
              <span className="px-2 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-700 rounded-full flex-shrink-0 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                No Limits
              </span>
            )}
          </div>
          
          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{plan.description}</p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            onClick={() => onEdit(plan)}
            className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors"
            title="Edit Plan"
          >
            <Edit className="w-4 h-4 text-indigo-500" />
          </button>
          <button 
            onClick={() => onDelete(plan.planId)}
            className="p-1.5 hover:bg-rose-50 rounded-lg transition-colors"
            title="Delete Plan"
          >
            <Trash2 className="w-4 h-4 text-rose-500" />
          </button>
        </div>
      </div>

      {/* Price */}
      <div className="mt-3 flex items-baseline gap-1">
        {yearlyPrice === 0 ? (
          <span className="text-2xl font-bold text-slate-900">Free</span>
        ) : (
          <>
            <span className="text-2xl font-bold text-slate-900">₹{yearlyPrice.toLocaleString()}</span>
            <span className="text-sm text-slate-500">/year</span>
          </>
        )}
      </div>

      {/* Tax Info */}
      <div className="mt-0.5 flex items-center gap-1">
        <span className={`text-xs ${hasTax ? 'text-slate-600' : 'text-slate-400'}`}>
          {hasTax ? `Tax: ${taxDisplay}` : 'No tax applied'}
        </span>
      </div>

      {/* Visibility Badge */}
      <div className="mt-2 flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${visibilityBadge.color}`}>
          {visibilityBadge.icon}
          {visibilityBadge.label}
        </span>
        
        {/* Status Badge */}
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${
          plan.isActive 
            ? 'text-emerald-600 bg-emerald-50 border-emerald-200' 
            : 'text-slate-500 bg-slate-100 border-slate-200'
        }`}>
          {plan.isActive ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          {plan.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Features - Always visible */}
      <div className="mt-4 grid grid-cols-2 gap-1.5">
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          <Users className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="truncate">{plan.features?.maxStudents === 0 ? 'Unlimited' : plan.features?.maxStudents || 0} Students</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          <Package className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="truncate">{plan.features?.maxStorageGB === 0 ? 'Unlimited' : plan.features?.maxStorageGB || 0} GB</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          <Users className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="truncate">{plan.features?.maxStaff === 0 ? 'Unlimited' : plan.features?.maxStaff || 0} Staff</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="truncate">{plan.features?.maxBranches === 0 ? 'Unlimited' : plan.features?.maxBranches || 0} Branches</span>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Zap className="w-3 h-3 text-amber-500" />
              {moduleCount} modules
            </span>
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Shield className="w-3 h-3 text-purple-500" />
              {roleCount} roles
            </span>
            {hasPlanLimits && (
              <span className="text-[10px] text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Updated: {formatDate(lastSynced)}
              </span>
            )}
          </div>
          
          {/* Expand/Collapse Button */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
          >
            {expanded ? 'Show less' : 'Show more'}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
            {/* Modules List */}
            {enabledModules.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
                  <Zap className="w-3 h-3 text-amber-500" />
                  Enabled Modules ({enabledModules.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {enabledModules.slice(0, 10).map((module) => (
                    <span key={module} className="px-2 py-0.5 text-[10px] bg-amber-50 text-amber-700 rounded-full">
                      {module.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  ))}
                  {enabledModules.length > 10 && (
                    <span className="px-2 py-0.5 text-[10px] bg-slate-100 text-slate-500 rounded-full">
                      +{enabledModules.length - 10} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Roles List */}
            {enabledRoles.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
                  <Shield className="w-3 h-3 text-purple-500" />
                  Enabled Roles ({enabledRoles.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {enabledRoles.slice(0, 8).map((role) => (
                    <span key={role} className="px-2 py-0.5 text-[10px] bg-purple-50 text-purple-700 rounded-full">
                      {role}
                    </span>
                  ))}
                  {enabledRoles.length > 8 && (
                    <span className="px-2 py-0.5 text-[10px] bg-slate-100 text-slate-500 rounded-full">
                      +{enabledRoles.length - 8} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Tenant Info */}
            <div className="flex items-center gap-4 text-xs text-slate-500 pt-1 border-t border-slate-100">
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {plan.activeTenants || 0} active tenants
              </span>
              {plan.trialTenants > 0 && (
                <span className="flex items-center gap-1 text-blue-600">
                  <Gift className="w-3 h-3" />
                  {plan.trialTenants} in trial
                </span>
              )}
              {plan.specialPricingCount > 0 && (
                <span className="flex items-center gap-1 text-emerald-600">
                  <Gift className="w-3 h-3" />
                  {plan.specialPricingCount} special prices
                </span>
              )}
            </div>

            {/* PlanLimits Details */}
            {hasPlanLimits && (
              <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-200">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-600">PlanLimits Details</span>
                  <span className="text-slate-400">v{limitsVersion}</span>
                </div>
                <div className="grid grid-cols-2 gap-1 mt-1 text-[10px] text-slate-500">
                  <span>Max Students: {plan.limits?.limits?.maxStudents || 0}</span>
                  <span>Max Staff: {plan.limits?.limits?.maxStaff || 0}</span>
                  <span>Max Branches: {plan.limits?.limits?.maxBranches || 1}</span>
                  <span>Storage: {plan.limits?.limits?.maxStorageGB || 0} GB</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanCard;